import { Component, OnInit, ElementRef, ChangeDetectorRef } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import moment from 'moment';
import { ViewTableComponent } from '../../base/view-table/view-table.component';
import { HttpParameter } from 'src/app/types/http-parameter';
import { PrintService } from 'src/app/services/print.service';
import { DialogService } from 'src/app/services/dialog.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { UtilityService } from 'src/app/services/utility.service';
import { FieldsService } from 'src/app/services/fields.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { FormBuilder } from '@angular/forms';
import { RouteEventsService } from 'src/app/route-events.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { PackagesService } from 'src/app/services/packages.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from 'src/app/services/user.service';
import { DomSanitizer } from '@angular/platform-browser';
import { Renderer2 } from '@angular/core';
import { RefreshService } from 'src/app/services/refresh.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyService } from '../../../services/currency.service';
@Component({
  selector: 'app-payments-due',
  templateUrl: './payments-due.component.html',
  styleUrls: ['./payments-due.component.css']
})
export class PaymentsDueComponent extends ViewTableComponent implements OnInit {

  currentPage: number;

  // owl date-range picker. start_date/end_date are sent to the /due endpoint as
  // the upcoming-window bounds; dateRange is the picker's bound value.
  start_date: string = '';
  end_date: string = '';
  dateRange: Date[] = [];

  // Payments due are always dated today or later, so block selecting past
  // dates. A past range would be AND-ed with the built-in
  // "payment_date >= today" filter and silently return zero rows.
  // Getter (not a captured value): "today" is the STUDIO's current date
  // (field_studio_timezone, loaded async, same source as day-view), at
  // start-of-day so the calendar's today cell isn't disabled by a
  // time-of-day component.
  get today(): Date {
    return this.reportDateService.studioTodayStart();
  }

  override displayedColumns: string[] = [
    'due_date',
    'student_name',
    'enrollment_title',
    'amount_due',
    'lessons_remaining',
    'instructor',
  ];

  constructor(
    private _printService: PrintService,
    public override _refreshService: RefreshService,
    public override _dialogService: DialogService,
    public override _entityRESTService: EntityRESTService,
    public override _utilityService: UtilityService,
    public override _fieldsService: FieldsService,
    public override _drupalRESTService: DrupalRESTService,
    public override _taxonomyService: TaxonomyService,
    public override _router: Router,
    public override _activatedRoute: ActivatedRoute,
    public override _authRESTService: AuthRESTService,
    public override _authService: AuthService,
    public override _menuRESTService: MenuRESTService,
    public override _formBuilder: FormBuilder,
    public override _routeEventsService: RouteEventsService,
    public override _permissionsService: PermissionsService,
    public override _renderer2: Renderer2,
    public override _configPages: ConfigPagesService,
    public override _packagesService: PackagesService,
    public override _snackBar: MatSnackBar,
    public override _userRESTService: UserService,
    public override _el: ElementRef,
    public override _changeDetectorRef: ChangeDetectorRef,
    public override _sanitizer: DomSanitizer,
    public override _studioConfigService: StudioConfigService,
    public override _componentRefreshService: ComponentRefreshService,
    public override _translateService: TranslateService,
    private currencyService: CurrencyService,
  ) {
    super(
      _refreshService,
      _dialogService,
      _entityRESTService,
      _utilityService,
      _fieldsService,
      _drupalRESTService,
      _taxonomyService,
      _router,
      _activatedRoute,
      _authRESTService,
      _authService,
      _menuRESTService,
      _formBuilder,
      _routeEventsService,
      _permissionsService,
      _renderer2,
      _configPages,
      _packagesService,
      _snackBar,
      _userRESTService,
      _el,
      _changeDetectorRef,
      _sanitizer,
      _studioConfigService,
      _componentRefreshService,
      _translateService
    );
  }

  // Helper method to format currency values
  private formatCurrency(value: number | string | null | undefined): string {
    return this._utilityService.formatCurrency(value);
  }

  // Format date for display
  private formatDate(dateString: string | null | undefined): string {
    return this.reportDateService.formatDate(dateString, 'N/A');
  }

  // Keep the date range filter out of the URL. Other filters (student,
  // instructor, etc.) still participate in URL sync via the base class.
  override updateRouterParams(routeParams?: any): void {
    if (routeParams) {
      const { start_date: _s, end_date: _e, ...rest } = routeParams;
      super.updateRouterParams(rest);
    } else {
      super.updateRouterParams(routeParams);
    }
  }

  // Handle a selection from the owl date-range picker. The value is a
  // [start, end] Date array (or empty/cleared).
  dateRangeChange(event: any): void {
    const range = Array.isArray(event?.value) ? event.value : [];
    if (range[0] && range[1]) {
      this.start_date = moment(range[0]).format('YYYY-MM-DD');
      this.end_date = moment(range[1]).format('YYYY-MM-DD');
    } else {
      this.start_date = '';
      this.end_date = '';
    }
    this.submitDateRange(this.start_date, this.end_date);
  }

  /**
   * Push the current date range into queryParams and trigger a fresh fetch.
   *
   * We call getData directly instead of leaning on ngSubmit because:
   * - the base onSubmit doesn't call getData itself, it relies on a URL-change
   *   subscription; and
   * - we intentionally don't sync start_date/end_date to the URL, so the
   *   subscription never fires for date changes.
   */
  private submitDateRange(start: string, end: string): void {
    this.queryParams = this.queryParams.filter(
      p => p.parameter !== 'start_date' && p.parameter !== 'end_date',
    );
    if (start && end) {
      this.queryParams.push(
        { parameter: 'start_date', value: start },
        { parameter: 'end_date', value: end },
      );
    }
    this.getData(this.queryParams);
  }

  /**
   * Custom print method for the payments due report.
   *
   * The on-screen table is server-paged (default 10 rows), so printing from
   * this.dataSource would only ever emit the current page. Re-fetch with a
   * single large page instead, so the printout covers every row the filters
   * matched.
   *
   * PRINT_ROW_LIMIT mirrors the backend's own ceiling: PaymentsDueReportController
   * clamps items_per_page to max(1, min($items, 200)) and applies range()
   * unconditionally, so 200 is the most rows this endpoint can ever return and
   * items_per_page=0 clamps to 1 rather than meaning "all rows": never send 0
   * here. (payments-made-encompassing uses 5000 because ITS endpoint has no such
   * clamp; that number does not transfer.) The cap is reachable, so onPrint
   * tells the user when the match count exceeds what was printed.
   */
  private static readonly PRINT_ROW_LIMIT = 200;

  override onPrint() {
    // Must happen here, in the click handler: the fetch below ends the user
    // gesture, so opening the window from its callback trips the popup blocker.
    const printWindow = this._printService.openPrintWindow();
    if (!printWindow) {
      this._snackBar.open(
        'Your browser blocked the print window. Allow pop-ups for this site and try again.',
        'Close', { duration: 8000 });
      return;
    }

    this.ShowProgressBar = true;

    const limit = PaymentsDueComponent.PRINT_ROW_LIMIT;
    const params = this.buildRequestParams([
      ...this.queryParams,
      { parameter: 'page', value: '0' },
      { parameter: 'items_per_page', value: limit.toString() },
    ]);

    this._drupalRESTService.httpGET('/api/payments-due-report/due', params)
      .subscribe({
        next: (data) => {
          this.ShowProgressBar = false;

          // A dead PHP worker surfaces as a 200 with an empty body, which would
          // otherwise be indistinguishable from a genuinely empty result set.
          if (!data || data['results'] === undefined) {
            printWindow.close();
            this._snackBar.open(
              'Could not build the printout. The report did not return. Try a narrower date range.',
              'Close', { duration: 8000 });
            return;
          }

          const rows = data['results'] || [];
          if (!rows.length) {
            printWindow.close();
            this._snackBar.open('No payments due found for the selected criteria.', 'Close', { duration: 5000 });
            return;
          }

          const total = Number(data['count'] ?? rows.length);
          if (total > rows.length) {
            this._snackBar.open(
              `This range matches ${total} payments due; printing the first ${rows.length}. Narrow the date range to print the rest.`,
              'Close', { duration: 8000 });
          }

          this.renderPrintout(rows, printWindow);
        },
        error: (error) => {
          this.ShowProgressBar = false;
          printWindow.close();
          this.handleError(error);
        }
      });
  }

  /**
   * Build and hand off the printable HTML for an already-fetched result set.
   */
  private renderPrintout(rows: any[], printWindow: Window | null) {
    // Build the HTML content for printing
    let content = `
      <div class="report-container">
        <h1 class="report-title">Payments Due Report</h1>

        <div class="report-section">
          <table class="report-table">
            <thead>
              <tr>
                <th>Due Date</th>
                <th>Student</th>
                <th>Enrollment</th>
                <th>Amount Due</th>
                <th>Lessons Remaining</th>
                <th>Teacher</th>
              </tr>
            </thead>
            <tbody>`;

    // Add rows for each upcoming installment. Callers guarantee a non-empty set.
    rows.forEach((row: any) => {
      content += `
          <tr>
            <td>${this.formatDate(row.due_date)}</td>
            <td>${this.escapeHtml(row.student_name) || 'N/A'}</td>
            <td>${this.escapeHtml(row.enrollment_title) || 'N/A'}</td>
            <td>${this.currencyService.format(row.amount_due)}</td>
            <td>${this.escapeHtml(row.lessons_remaining) || '0'}</td>
            <td>${this.escapeHtml(row.instructor)}</td>
          </tr>`;
    });

    content += `
            </tbody>
          </table>
        </div>
      </div>`;

    // Define custom styles for the report
    const styles = `
      .report-container {
        font-family: Arial, sans-serif;
        margin: 0 auto;
        padding: 20px;
        max-width: 100%;
      }
      .report-title {
        font-size: 24px;
        text-align: center;
        margin-bottom: 20px;
        color: #333;
      }
      .report-section {
        margin-bottom: 30px;
        page-break-inside: avoid;
      }
      .report-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 20px;
      }
      .report-table th, .report-table td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
      }
      .report-table th {
        background-color: #f2f2f2;
        font-weight: bold;
      }
      .report-table tr:nth-child(even) {
        background-color: #f9f9f9;
      }
      .no-data {
        text-align: center;
        font-style: italic;
        color: #999;
      }
      @media print {
        .report-section {
          page-break-inside: avoid;
        }
      }
    `;

    // Use the PrintService to print the report
    this._printService.print(content, {
      title: 'Payments Due Report',
      styles: styles,
      showSchoolName: true
    }, printWindow);
  }

  override resetForm() {
    // Clear all stored params
    this.queryParams = [];
    this.sortParams = undefined;

    // Reset pagination
    if (this.paginator) {
      this.paginator.pageIndex = 0;
      this.paginationIndex = 0;  // Reset the pagination index
      this.pageSize = 10;  // Reset page size
    }

    // Clear the form
    this.form.resetForm();

    // Clear the date range picker
    this.dateRange = [];
    this.start_date = '';
    this.end_date = '';

    // Clear any autocomplete options
    this.autoCompleteStudentAccountOptions = [];

    // Make a fresh request with explicitly empty params
    this.getData([]);

    // Update router last
    this.updateRouterParams({});
  }

  override pageChanged(event: any) {
    // Update pagination state
    this.paginationIndex = event.pageIndex;
    this.pageSize = event.pageSize;

    // Always include pagination parameters
    const paginationParams: HttpParameter[] = [
      { parameter: 'page', value: this.paginationIndex.toString() },
      { parameter: 'items_per_page', value: this.pageSize.toString() }
    ];

    // Filter out any existing pagination params from queryParams
    const filteredQueryParams = this.queryParams.filter(param =>
      param.parameter !== 'page' && param.parameter !== 'items_per_page'
    );

    // Combine filtered query params with new pagination params
    const combinedParams = [...filteredQueryParams, ...paginationParams];
    this.getData(combinedParams);
  }

  override getData(params: HttpParameter[]) {
    this.ShowProgressBar = true;

    // Store only non-pagination params in queryParams
    this.queryParams = params.filter(param =>
      param.parameter !== 'page' && param.parameter !== 'items_per_page'
    );

    // Add current pagination state to params if not present
    let finalParams = [...params];
    if (!params.some(p => p.parameter === 'page')) {
      finalParams.push(
        { parameter: 'page', value: this.paginationIndex.toString() },
        { parameter: 'items_per_page', value: this.pageSize.toString() }
      );
    }

    finalParams = this.buildRequestParams(finalParams);

    // ami_payments_due_report: one row per upcoming unpaid installment. The
    // studio is auto-injected by DrupalRESTService; start_date/end_date (set by
    // the owl range picker) narrow the upcoming window and are read directly.
    const endpoint: string = "/api/payments-due-report/due";

    this._drupalRESTService.httpGET(endpoint, finalParams)
      .subscribe({
        next: (data) => {
          this.ShowProgressBar = false;
          this.dataSource = new MatTableDataSource(data['results']);

          if (data?.['results'] && this.paginator) {
            this.paginator.length = data?.['count'] ?? 0;
            this.paginator.pageSize = this.pageSize ?? 0;
            this.paginator.pageIndex = this.paginationIndex;
          }
        },
        error: (error) => this.handleError(error)
      });
  }

  /**
   * Apply the sort parameter on top of the caller's filter/pagination params.
   * Shared by getData() and onPrint() so the printed report is guaranteed to
   * reflect the same filters as the on-screen table.
   *
   * The date range needs no translation here: start_date/end_date are already
   * YYYY-MM-DD (set by dateRangeChange) and the endpoint reads them directly.
   */
  private buildRequestParams(params: HttpParameter[]): HttpParameter[] {
    const finalParams = [...params];

    // Add sorting parameters if available
    if (this.sortParams !== undefined) {
      finalParams.push(this.sortParams);
    }

    return finalParams;
  }

  private escapeHtml(value: string | null | undefined): string {
    if (value === null || value === undefined) { return ''; }
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  override ngOnInit() {
    // Initialize pagination values
    this.paginationIndex = 0;
    this.pageSize = 10;  // Set default page size

    // Call parent ngOnInit if it exists
    super.ngOnInit();

    // Initial data load
    this.getData([
      { parameter: 'page', value: this.paginationIndex.toString() },
      { parameter: 'items_per_page', value: this.pageSize.toString() }
    ]);
  }

  override setupPagination(data: any, params: { parameter: string; value: string; }[]) {
    const pageParam = params.find(param => param.parameter === 'page');
    if (this.paginator) {
      this.paginator.length = data?.['count'] ?? 0;
      this.paginator.pageSize = this.pageSize;
      this.paginator.pageIndex = pageParam ? parseInt(pageParam.value, 10) : 0;
    }
    // DO NOT call updateRouterParams here - prevents duplicate requests
  }
}
